"use client";

import { FAQContainer, type FAQItemData } from "@/components/faq/faq-container";
import { getFAQContentParams } from "@/components/faq/content-params";
import { H2 } from "@/components/ui/heading";
import { valuesWithAppName } from "@/lib/i18n";
import { useTranslations } from "next-intl";

export function ValuationToolFAQ() {
  const t = useTranslations();
  const contentParams = getFAQContentParams(t);
  const LARGE_FAQ_THRESHOLD = 10;

  const faqs: FAQItemData[] = [
    {
      itemId: "how-valuation",
      title: t("tools.valuation.faq.howValuation.question"),
      content: t.rich("tools.valuation.faq.howValuation.answer", contentParams),
    },

    {
      itemId: "is-it-free",
      title: t(
        "tools.valuation.faq.isItFree.question",
        valuesWithAppName(t, {}),
      ),
      content: t.rich("tools.valuation.faq.isItFree.answer", contentParams),
    },

    {
      itemId: "trust-estimate",
      title: t("tools.valuation.faq.trustEstimate.question"),
      content: t.rich(
        "tools.valuation.faq.trustEstimate.answer",
        contentParams,
      ),
    },

    {
      itemId: "numbers-needed",
      title: t("tools.valuation.faq.numbersNeeded.question"),
      content: t.rich(
        "tools.valuation.faq.numbersNeeded.answer",
        contentParams,
      ),
    },

    {
      itemId: "how-long",
      title: t("tools.valuation.faq.howLong.question"),
      content: t.rich("tools.valuation.faq.howLong.answer", contentParams),
    },

    {
      itemId: "which-industries",
      title: t("tools.valuation.faq.whichIndustries.question"),
      content: t.rich(
        "tools.valuation.faq.whichIndustries.answer",
        contentParams,
      ),
    },

    {
      itemId: "formal-appraisal",
      title: t(
        "tools.valuation.faq.formalAppraisal.question",
        valuesWithAppName(t, {}),
      ),
      content: t.rich(
        "tools.valuation.faq.formalAppraisal.answer",
        contentParams,
      ),
    },

    {
      itemId: "financials-private",
      title: t("tools.valuation.faq.financialsPrivate.question"),
      content: t.rich(
        "tools.valuation.faq.financialsPrivate.answer",
        contentParams,
      ),
    },

    {
      itemId: "value-factors",
      title: t("tools.valuation.faq.valueFactors.question"),
      content: t.rich("tools.valuation.faq.valueFactors.answer", contentParams),
    },

    {
      itemId: "valuation-range",
      title: t(
        "tools.valuation.faq.valuationRenge.question",
        valuesWithAppName(t, {}),
      ),
      content: t.rich(
        "tools.valuation.faq.valuationRenge.answer",
        contentParams,
      ),
    },

    {
      itemId: "list-business",
      title: t(
        "tools.valuation.faq.listBusiness.question",
        valuesWithAppName(t, {}),
      ),
      content: t.rich("tools.valuation.faq.listBusiness.answer", contentParams),
    },

    {
      itemId: "sde",
      title: t("tools.valuation.faq.sde.question"),
      content: t.rich("tools.valuation.faq.sde.answer", contentParams),
    },

    {
      itemId: "location-affect",
      title: t("tools.valuation.faq.locationAffect.question"),
      content: t.rich(
        "tools.valuation.faq.locationAffect.answer",
        contentParams,
      ),
    },

    {
      itemId: "update-valuation",
      title: t("tools.valuation.faq.updateValuation.question"),
      content: t.rich(
        "tools.valuation.faq.updateValuation.answer",
        contentParams,
      ),
    },

    {
      itemId: "valuation-privacy",
      title: t("tools.valuation.faq.valuationPrivacy.question"),
      content: t.rich(
        "tools.valuation.faq.valuationPrivacy.answer",
        contentParams,
      ),
    },
  ];

  const isLargeFaqSet = faqs.length > LARGE_FAQ_THRESHOLD;
  const midpoint = Math.ceil(faqs.length / 2);
  const leftFaqs = faqs.slice(0, midpoint);
  const rightFaqs = faqs.slice(midpoint);

  return (
    <section className="rounded-2xl border border-border/70 bg-card p-5 md:p-8">
      <div className="mb-6 flex flex-col gap-2 md:mb-8">
        <H2>{t("tools.valuation.faq.title")}</H2>
        <p className="text-sm text-muted-foreground md:text-base">
          Everything sellers ask before starting a valuation.
        </p>
      </div>

      {!isLargeFaqSet ? (
        <FAQContainer groupId="valuation_tool_faq" items={faqs} variant="rows" />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
          <FAQContainer
            groupId="valuation_tool_faq_left"
            items={leftFaqs}
            variant="rows"
          />
          <FAQContainer
            groupId="valuation_tool_faq_right"
            items={rightFaqs}
            variant="rows"
          />
        </div>
      )}
    </section>
  );
}
